import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { BarChart3, TrendingUp, ShieldCheck, AlertTriangle, Flag, Loader2, Wrench, Euro, PiggyBank } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';
import HumedisityLink from '../components/app/HumedisityLink';

const fmt = (n) => new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(n || 0);
const eur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

const Kpi = ({ icon: Icon, label, value, sub, tone = 'green' }) => {
  const tones = {
    green: 'text-[#A3FF12] border-[#A3FF12]/30 bg-[#A3FF12]/5',
    red: 'text-red-400 border-red-500/30 bg-red-500/5',
    orange: 'text-orange-400 border-orange-500/30 bg-orange-500/5',
    purple: 'text-purple-300 border-purple-500/30 bg-purple-500/5',
  };
  return (
    <div className={`border rounded-2xl p-4 ${tones[tone]}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-80"><Icon size={14}/> {label}</div>
      <div className={`text-3xl font-black mt-2 ${tones[tone].split(' ')[0]}`}>{value}</div>
      {sub && <div className="text-white/50 text-xs mt-1">{sub}</div>}
    </div>
  );
};

const MantenimientoDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/maintenance/dashboard');
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const { data: created } = await api.post('/incidents/scan-sites');
      alert(`Escaneo completado. ${created.length} incidencias/alertas generadas automáticamente.`);
      await load();
    } catch (e) {
      alert('Error al escanear: ' + (e.response?.data?.detail || e.message));
    } finally { setScanning(false); }
  };

  if (loading || !data) {
    return <div className="p-8 flex items-center justify-center min-h-[60vh]"><Loader2 className="animate-spin text-[#A3FF12]" size={32}/></div>;
  }

  const p = data.projection;
  const compareData = [
    { name: 'Sin preventivo', 'Correctivos 5a': p.corrective_5y_no_preventive, 'Preventivo': 0 },
    { name: 'Con preventivo', 'Correctivos 5a': p.corrective_5y_with_preventive, 'Preventivo': p.preventive_5y },
  ];
  const savingsData = [
    { name: 'Ahorro técnico', value: Math.max(p.technical_savings_5y, 0) },
    { name: 'Ahorro contractual (10%)', value: p.contractual_savings_low_5y },
    { name: 'Extra (15% escenario)', value: Math.max(p.contractual_savings_high_5y - p.contractual_savings_low_5y, 0) },
  ];
  const typeData = Object.entries(data.by_type).map(([k, v]) => ({ name: k, value: v }));
  const priorityData = [
    { name: 'Alta', value: data.priority_breakdown.alta },
    { name: 'Media', value: data.priority_breakdown.media },
    { name: 'Baja', value: data.priority_breakdown.baja },
  ];
  const COLORS = ['#A3FF12', '#f59e0b', '#ef4444', '#a855f7', '#3b82f6', '#10b981', '#ec4899'];

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="maintenance-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12]">MANTENIMIENTO</div>
          <h1 className="text-3xl sm:text-4xl font-black mt-1">Plan preventivo integral</h1>
          <p className="text-white/50 text-sm mt-1">Correctivo vs Preventivo · impacto técnico y contractual a 5 años.</p>
        </div>
        <button onClick={scan} disabled={scanning} data-testid="btn-scan-sites"
          className="flex items-center gap-2 bg-[#A3FF12] text-black font-black px-4 py-2.5 rounded-xl hover:bg-[#8ee00a] disabled:opacity-50 text-sm">
          {scanning ? <Loader2 className="animate-spin" size={16}/> : <Wrench size={16}/>}
          {scanning ? 'Escaneando…' : 'Auto-detectar incidencias'}
        </button>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={BarChart3} label="Emplazamientos" value={fmt(data.sites_total)} sub={`Tasa incidencia ${(data.incidence_rate*100).toFixed(1)}%`} />
        <Kpi icon={AlertTriangle} label="Incidencias abiertas" value={fmt(data.incidents_open)} sub={`${data.incidents_resolved} resueltas`} tone="red"/>
        <Kpi icon={Flag} label="Conflictivos" value={fmt(data.conflictive_active)} sub="Riesgo contractual activo" tone="orange"/>
        <Kpi icon={ShieldCheck} label="Preventivo planificado" value={fmt(data.preventive_planned)} sub={`${data.preventive_completed} completadas`} tone="green"/>
      </div>

      {/* Humedisity — peritaje IA de incidencias */}
      <HumedisityLink/>

      {/* Correctivo vs Preventivo */}
      <div className="bg-black/40 border border-white/10 rounded-2xl p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} className="text-[#A3FF12]"/>
          <h2 className="text-lg font-black">Correctivo vs Preventivo (proyección 5 años)</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={compareData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333"/>
                <XAxis dataKey="name" stroke="#888" fontSize={12}/>
                <YAxis stroke="#888" fontSize={11} tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`}/>
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #333' }} formatter={(v) => eur(v)}/>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
                <Bar dataKey="Correctivos 5a" fill="#ef4444" radius={[6,6,0,0]}/>
                <Bar dataKey="Preventivo" fill="#A3FF12" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-red-500/5 border border-red-500/20 rounded-xl">
              <div>
                <div className="text-xs uppercase text-red-300/80">Correctivo sin plan (5 años)</div>
                <div className="text-2xl font-black text-red-400 mt-1">{eur(p.corrective_5y_no_preventive)}</div>
              </div>
              <Euro size={28} className="text-red-500/50"/>
            </div>
            <div className="flex justify-between items-center p-3 bg-[#A3FF12]/5 border border-[#A3FF12]/20 rounded-xl">
              <div>
                <div className="text-xs uppercase text-[#A3FF12]/80">Preventivo (5 años)</div>
                <div className="text-2xl font-black text-[#A3FF12] mt-1">{eur(p.preventive_5y)}</div>
                <div className="text-white/40 text-xs mt-0.5">reduce incidencias ~70%</div>
              </div>
              <ShieldCheck size={28} className="text-[#A3FF12]/50"/>
            </div>
            <div className="flex justify-between items-center p-3 bg-white/5 border border-white/10 rounded-xl">
              <div>
                <div className="text-xs uppercase text-white/60">Ahorro técnico neto</div>
                <div className="text-2xl font-black text-white mt-1">{eur(p.technical_savings_5y)}</div>
              </div>
              <PiggyBank size={28} className="text-white/40"/>
            </div>
          </div>
        </div>
      </div>

      {/* Contractual & pie */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-black/40 border border-white/10 rounded-2xl p-4 sm:p-6">
          <h2 className="text-lg font-black mb-1">Impacto en renegociaciones</h2>
          <p className="text-white/50 text-xs mb-4">Propietarios satisfechos → 10-15% reducción por ciclo (5a).</p>
          <div className="space-y-2">
            <div className="flex justify-between items-center p-3 bg-white/5 border border-white/10 rounded-xl">
              <span className="text-white/70">Escenario conservador (10%)</span>
              <span className="text-[#A3FF12] font-black text-xl">{eur(p.contractual_savings_low_5y)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-[#A3FF12]/5 border border-[#A3FF12]/30 rounded-xl">
              <span className="text-white/70">Escenario óptimo (15%)</span>
              <span className="text-[#A3FF12] font-black text-xl">{eur(p.contractual_savings_high_5y)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-[#A3FF12]/20 to-[#A3FF12]/5 border border-[#A3FF12]/40 rounded-xl mt-3">
              <span className="text-white font-bold">Ahorro TOTAL combinado (rango)</span>
              <span className="text-[#A3FF12] font-black text-lg">{eur(p.total_savings_low_5y)} – {eur(p.total_savings_high_5y)}</span>
            </div>
          </div>
        </div>

        <div className="bg-black/40 border border-white/10 rounded-2xl p-4 sm:p-6">
          <h2 className="text-lg font-black mb-1">Distribución por prioridad</h2>
          <p className="text-white/50 text-xs mb-4">Incidencias registradas.</p>
          {priorityData.every((p) => p.value === 0) ? (
            <p className="text-white/40 text-sm py-8 text-center">Sin incidencias registradas todavía.</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={priorityData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                    {priorityData.map((_, i) => <Cell key={i} fill={['#ef4444','#f59e0b','#22c55e'][i]}/>)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#111', border: '1px solid #333' }}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Anomalías detectadas */}
      {typeData.length > 0 && (
        <div className="bg-black/40 border border-white/10 rounded-2xl p-4 sm:p-6">
          <h2 className="text-lg font-black mb-4">Anomalías por tipo</h2>
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={typeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#333"/>
                <XAxis type="number" stroke="#888" fontSize={11}/>
                <YAxis type="category" dataKey="name" stroke="#888" fontSize={11} width={140}/>
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #333' }}/>
                <Bar dataKey="value" fill="#A3FF12" radius={[0,6,6,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default MantenimientoDashboard;
